import { FunctionComponent, useCallback, useEffect, useState } from 'react'

import styled, { css, useTheme } from 'styled-components'

import config from '@config'
import { desktopCSS, mobileCSS } from '@measures/responsive'
import { getDeviceType } from '@utils/deviceType'
import { getLocalStorageItem, setLocalStorageItem } from '@utils/localStorage'
import translate from '@i18n'
import useFeatureFlagsSkeleton from '@hooks/useFeatureFlagsSkeleton'

import IconButton from '../Buttons/IconButton'
import PrimaryCTABlink from '../Buttons/PrimaryCTABlink'
import SvgIcon from '../SvgIcon'
import QRCode from './QRCode'

const {
  appLinks: { ios, android },
  fmtOnboarding: {
    localStorageHideFmtOnboardingContainerKeySelectTeamList,
    localStorageHideFmtOnboardingContainerKeyTeamsTab,
    moreInfoLink,
  },
} = config

export type StyledFmTOnboardingContainerProps = {
  isInDrawer: boolean
  isInMyTeams?: boolean
}

export type FmTOnboardingContainerProps = {
  position: 'sportCompetitorsList' | 'selectTeam' | 'myTeams'
  setShowFmtOnboardingContainer: React.Dispatch<React.SetStateAction<boolean>>
  showFmtOnboardingContainer: boolean
}

const StyledWrapper = styled.div<StyledFmTOnboardingContainerProps>`
  ${({
    theme: {
      colors: { fillSport, staticBlack },
      spacing: { spacing16, spacing8 },
    },
    isInDrawer,
    isInMyTeams,
  }) => css`
    border-radius: ${spacing8};
    padding: ${isInDrawer
      ? `${spacing8} ${spacing8} ${spacing16} ${spacing8}`
      : spacing16};
    margin-bottom: ${isInMyTeams ? spacing16 : '0'};
    background: linear-gradient(85deg, ${staticBlack} -68%, ${fillSport} 68%);
    display: grid;
    gap: ${spacing8};
    grid-template-columns: auto 1fr auto;
  `}
`

const StyledBlickIcon = styled(SvgIcon)`
  ${({
    theme: {
      spacing: { spacing8, spacing40 },
    },
  }) => css`
    min-width: ${spacing40};
    border-radius: ${spacing8};
    align-self: flex-start;
  `}
`
const StyledMoreInfoButton = styled(
  PrimaryCTABlink,
)<StyledFmTOnboardingContainerProps>`
  ${({
    theme: {
      spacing: { spacing40 },
      colors: { staticWhite },
    },
    isInDrawer,
  }) => css`
    grid-area: 2 / 1 / 3 / 2;
    margin-left: ${isInDrawer ? '0' : spacing40};

    ${mobileCSS(css`
      background-color: transparent;
      border: 1px solid ${staticWhite};
      color: ${staticWhite};
      @media (hover: hover) {
        &:hover:not([disabled]) {
          background-color: transparent;
        }
      }

      &:focus-visible {
        background-color: transparent;
      }
    `)}
  `}
`

const StyledDownloadAppButton = styled(PrimaryCTABlink)`
  display: none;
  grid-area: 2 / 2 / 3 / 4;
  ${mobileCSS(css`
    display: flex;
    text-align: center;
  `)};
`

const StyledBlickLogoAndTitleWrapper = styled.div<StyledFmTOnboardingContainerProps>`
  ${({
    theme: {
      spacing: { spacing8 },
    },
    isInDrawer,
  }) => css`
    display: flex;
    gap: ${spacing8};
    align-items: center;
    grid-area: ${isInDrawer ? '1 / 1 / 2 / 3' : '1 / 1 / 2 / 2'};

    ${mobileCSS(css`
      grid-area: 1 / 1 / 2 / 3;
    `)}
  `}
`

const StyledCloseButton = styled(IconButton)<StyledFmTOnboardingContainerProps>`
  ${({
    theme: {
      spacing: { spacing24 },
    },
    isInDrawer,
  }) => css`
    width: ${spacing24};
    height: ${spacing24};
    grid-area: 1 / 3 / 2 / 4;
    ${mobileCSS(css`
      ${isInDrawer && 'display: none;'}
    `)}
  `}
`

const StyledContainerTitle = styled.div`
  ${({
    theme: {
      typography: {
        headings: {
          xsmall: { bundledCSS: xsmallHeadingCSS },
          xxsmall: { bundledCSS: xxsmallHeadingCSS },
        },
      },
      colors: { staticWhite },
    },
  }) => css`
    white-space: pre-wrap;
    ${xxsmallHeadingCSS};
    color: ${staticWhite};
    ${desktopCSS(css`
      ${xsmallHeadingCSS};
    `)}
  `}
`

const FmTOnboardingContainer: FunctionComponent<
  FmTOnboardingContainerProps
> = ({
  position,
  setShowFmtOnboardingContainer,
  showFmtOnboardingContainer,
}) => {
  const theme = useTheme()
  const isIos = getDeviceType() === 'ios'
  const { isFollowMyTeamEnabled } = useFeatureFlagsSkeleton()

  const [appLink, setAppLink] = useState('')
  const isInDrawer = position === 'selectTeam' || position === 'myTeams'

  useEffect(() => {
    setAppLink(isIos ? ios : android)
  }, [isIos])

  useEffect(() => {
    const hasBeenDismissed = getLocalStorageItem(
      position === 'selectTeam'
        ? localStorageHideFmtOnboardingContainerKeySelectTeamList
        : localStorageHideFmtOnboardingContainerKeyTeamsTab,
    )

    setShowFmtOnboardingContainer(
      position === 'myTeams' ? true : !hasBeenDismissed,
    )
  }, [position, setShowFmtOnboardingContainer])

  const onClose = useCallback(() => {
    const localStorageHideFmtOnboardingContainerKey =
      position === 'selectTeam'
        ? localStorageHideFmtOnboardingContainerKeySelectTeamList
        : localStorageHideFmtOnboardingContainerKeyTeamsTab

    setShowFmtOnboardingContainer(false)
    setLocalStorageItem(localStorageHideFmtOnboardingContainerKey, position)
  }, [position, setShowFmtOnboardingContainer])

  return showFmtOnboardingContainer && isFollowMyTeamEnabled ? (
    <StyledWrapper isInDrawer={isInDrawer} isInMyTeams={position === 'myTeams'}>
      <StyledBlickLogoAndTitleWrapper isInDrawer={isInDrawer}>
        <StyledBlickIcon iconName="fmt-blick-logo" size={40} />
        <StyledContainerTitle>
          {translate(
            position === 'myTeams'
              ? 'fmtOnboardingContainer.myTeams.title'
              : 'fmtOnboardingContainer.selectTeam.title',
          )}
        </StyledContainerTitle>
      </StyledBlickLogoAndTitleWrapper>
      <StyledMoreInfoButton
        isInDrawer={isInDrawer}
        colorVariant="white"
        size="small"
        href={moreInfoLink}
        target="_blank"
      >
        {translate('fmtOnboardingContainer.button.moreInfo')}
      </StyledMoreInfoButton>
      <StyledDownloadAppButton
        colorVariant="white"
        size="small"
        href={appLink}
        target="_blank"
        rel="nofollow noopener"
      >
        {translate('fmtOnboardingContainer.button.downloadApp')}
      </StyledDownloadAppButton>
      <QRCode isInDrawer={isInDrawer} />
      {position !== 'myTeams' && (
        <StyledCloseButton
          isInDrawer={isInDrawer}
          type="button"
          iconName="xmark-large"
          iconSize={24}
          iconColor={theme.colors.staticWhite}
          ariaLabel="close"
          onClick={onClose}
        />
      )}
    </StyledWrapper>
  ) : null
}

export default FmTOnboardingContainer
